import { Priority, User } from "@/types";
import { AlertCircle, Target, X } from "lucide-react";
import { PriorityCard } from "./PriorityCard";
import { PppGroupHeader } from "./PppGroupHeader";
import { groupByPpp } from "./group-by-ppp";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";

interface PriorityListProps {
    priorities: Priority[];
    isLoading: boolean;
    isError: boolean;
    onView?: (priority: Priority) => void;
    onEdit: (priority: Priority) => void;
    onDelete: (priority: Priority) => void;
    currentUser?: User | null;
    isAdmin?: boolean;
    hasActiveFilters?: boolean;
    onClearFilters?: () => void;
    /** Set when the empty list is only empty because `done` items are hidden by the default filter. */
    onShowDone?: () => void;
    /** Group under each item's best PPP (server order preserved). Off for the flat Candidates list. */
    grouped?: boolean;
}

export const PriorityList = ({
    priorities, isLoading, isError, onView, onEdit, onDelete, currentUser, isAdmin,
    hasActiveFilters, onClearFilters, onShowDone, grouped = true,
}: PriorityListProps) => {
    if (isLoading) {
        return (
            <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                    <div key={i} className="rounded-xl border border-border bg-card p-5">
                        <div className="flex items-start justify-between gap-4">
                            <div className="flex flex-1 items-start gap-4">
                                <Skeleton className="h-10 w-10 rounded-lg" />
                                <div className="flex-1 space-y-2">
                                    <Skeleton className="h-4 w-24" />
                                    <Skeleton className="h-6 w-3/4" />
                                    <Skeleton className="h-4 w-full" />
                                </div>
                            </div>
                            <Skeleton className="h-6 w-20 rounded-full" />
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    if (isError) {
        return (
            <div className="rounded-xl border border-destructive/50 bg-destructive/5 p-12 text-center">
                <AlertCircle className="mx-auto mb-4 h-12 w-12 text-destructive" />
                <h3 className="mb-2 font-semibold">Failed to load deliverables</h3>
                <p className="text-sm text-muted-foreground">There was an error fetching the deliverables. Please try again.</p>
            </div>
        );
    }

    if (priorities.length === 0) {
        return (
            <div className="rounded-xl border border-dashed border-border bg-muted/50 p-12 text-center">
                <Target className="mx-auto mb-4 h-12 w-12 text-muted-foreground/50" />
                <h3 className="mb-2 font-semibold">No deliverables found</h3>
                <p className="text-sm text-muted-foreground">
                    {hasActiveFilters ? "No deliverables match your current filters." : onShowDone ? "Nothing in flight — done items are hidden." : "Create your first deliverable to get started."}
                </p>
                {!hasActiveFilters && onShowDone && (
                    <Button variant="outline" size="sm" className="mt-4" onClick={onShowDone}>Show done</Button>
                )}
                {hasActiveFilters && onClearFilters && (
                    <Button variant="outline" size="sm" className="mt-4 gap-1.5" onClick={onClearFilters}>
                        <X className="h-3.5 w-3.5" /> Clear all filters
                    </Button>
                )}
            </div>
        );
    }

    const card = (priority: Priority, omitPppPid?: string) => (
        <PriorityCard
            key={priority.pid}
            priority={priority}
            onView={onView}
            onEdit={onEdit}
            onDelete={onDelete}
            currentUser={currentUser}
            isAdmin={isAdmin}
            omitPppPid={omitPppPid}
        />
    );

    if (!grouped) return <div className="space-y-3">{priorities.map((p) => card(p))}</div>;

    return (
        <div className="space-y-5">
            {groupByPpp(priorities).map((group) => (
                <section key={group.key} className="space-y-3">
                    <PppGroupHeader ppp={group.ppp} count={group.items.length} />
                    {group.items.map((p) => card(p, group.ppp?.pid))}
                </section>
            ))}
        </div>
    );
};
